import {z} from "zod"
import {zodResolver} from "@hookform/resolvers/zod"
import {useForm} from "react-hook-form"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {ReportForm} from "@/types/ReportForm.ts";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {cn} from "@/lib/utils.ts";
import {Check, ChevronsUpDown} from "lucide-react";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command.tsx";
import {Cve} from "@/types/Cve.ts";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Combobox} from "@/components/ui/combobox.tsx";
import {FormSubmission} from "@/types/FormSubmssion";
import {toast} from "react-hot-toast";
import {Header} from "@/components/Header.tsx";
import {PoweredBy} from "@/components/PoweredBy.tsx";
import settings from "config.json";
import {Turnstile} from "@marsidev/react-turnstile";
import {ExposedError} from "@/types/Errors.ts";

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

const formSchema = z.object({
  name: z.string().optional(),
  email: z.string().email(),
  subject: z.string().min(5).max(100),
  scope: z.string().optional(),
  severity: z.string(
    {
      required_error: "Please select a severity level"
    }),
  vulnerability: z.string().optional(),
  known_cve: z.string().optional(),
  description: z.string().min(10),
  attachments: z.array(z.custom<File>((file) => file instanceof File))
    .refine(
      (files) => files.every((file) => file.size <= MAX_FILE_SIZE),
      "Maximum file size is 10MB each"
    ).optional(),
});

const severities = [
  {label: "Information", value: "info"},
  {label: "Low", value: "low"},
  {label: "Medium", value: "medium"},
  {label: "High", value: "high"},
  {label: "Critical", value: "critical"},

] as const


export const FormPage = () => {
  const [reportForm, setReportForm] = useState<ReportForm | null>(null);
  const {id} = useParams();
  const [knownCves, setKnownCves] = useState<Cve[]>([]);
  const [cveOpen, setCveOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    ReportForm.fetchReportForm({form_id: id})
      .then((reportForm) => {
        setReportForm(reportForm);
      }).catch((error: ExposedError) => {
      if (error.code === "NotFound") {
        navigate("/404", {replace: false});
      }
    });
  }, [id]);

  const searchCves = async (query: string) => {
    Cve.find(query).then((cves) => {
      setKnownCves(cves);
    });
  }

  const form = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        name: "",
        email: "",
        subject: "",
        scope: "",
        severity: "info",
        vulnerability: "",
        known_cve: "",
        description: "",
        attachments: [],
      }
    }
  )

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!id) return;

    const formSubmission = new FormSubmission({
      ...values,
    });

    if (!token) {
      toast.error("Please complete the captcha to submit the form");
      return;
    }

    toast.promise(FormSubmission.create({form_id: id, form_submission: formSubmission, token: token}),
      {
        loading: "Submitting...",
        success: "Submitted successfully",
      }).then(() => {
      navigate(`/reports/${id}/success`);
    }).catch((error) => {
      toast.error("Failed to submit the form " + error.message);
    });
  }

  if (!reportForm) return;

  return (
    <div>
      <Header reportForm={reportForm}/>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="name"
            render={({field}) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your Name" {...field} />
                </FormControl>
                <FormMessage/>
              </FormItem>
            )}
          />
          <FormField
            name={"email"} control={form.control}
            render={({field}) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Your Email" {...field} />
                </FormControl>
                <FormDescription>
                  We will use this email to contact you
                </FormDescription>
                <FormMessage/>
              </FormItem>
            )}/>
          <FormField
            name={"subject"} control={form.control}
            render={({field}) => (
              <FormItem>
                <FormLabel>Subject</FormLabel>
                <FormControl>
                  <Input placeholder="Subject" {...field} />
                </FormControl>
                <FormDescription>
                  A summary of the vulnerability discovered
                </FormDescription>
                <FormMessage/>
              </FormItem>
            )}/>
          {reportForm.allow_severity ? (
            <FormField
              name={"severity"} control={form.control}
              render={({field}) => (
                <FormItem className={"flex flex-col"}>
                  <FormLabel>Severity</FormLabel>
                  <FormControl>
                    <Combobox size={"sm"}
                              items={severities as any}
                              value={field.value}
                              name={"severity"}
                              onSelect={(item) => {
                                if (!item) return;
                                form.setValue("severity", item.value)
                              }}/>
                  </FormControl>
                  <FormMessage/>
                </FormItem>
              )}/>
          ) : null}
          {(reportForm.allow_vulnerability && reportForm.vulnerabilities) ? (
            <FormField
              name={"vulnerability"} control={form.control}
              render={({field}) => (
                <FormItem className={"flex flex-col"}>
                  <FormLabel>Vulnerability</FormLabel>
                  <FormControl>
                    <Combobox size={"lg"}
                              items={
                                reportForm?.vulnerabilities ?
                                  reportForm.vulnerabilities?.map((vulnerability) => ({
                                    label: vulnerability.name,
                                    value: vulnerability.unique_name
                                  })) : []
                              }
                              value={field.value} name={"vulnerability"}
                              onSelect={(item) => {
                                if (!item) return;
                                form.setValue("vulnerability", item.value)
                              }}/>
                  </FormControl>
                  <FormMessage/>
                  <FormDescription>
                    If the vulnerability type is known, please select it
                  </FormDescription>
                </FormItem>
              )}/>
          ) : null}
          {(reportForm.allow_scope && reportForm.scopes) ? (
            <FormField
              name={"scope"} control={form.control}
              render={({field}) => (
                <FormItem className={"flex flex-col"}>
                  <FormLabel>Scope</FormLabel>
                  <FormControl>
                    <Combobox size={"md"}
                              searchable
                              items={
                                reportForm?.scopes ?
                                  reportForm.scopes?.map((scope) => ({
                                    label: scope.name,
                                    value: scope.unique_name
                                  })) : []
                              }
                              value={field.value} name={"scope"}
                              onSelect={(item) => {
                                if (!item) return;
                                form.setValue("scope", item.value)
                              }}/>
                  </FormControl>
                  <FormMessage/>
                  <FormDescription>
                    Which part of the system is affected by the vulnerability
                  </FormDescription>
                </FormItem>
              )}/>
          ) : null}
          {reportForm.allow_cve ? (
            <FormField
              name={"known_cve"} control={form.control}
              render={({field}) => (
                <FormItem className={"flex flex-col"}>
                  <FormLabel>Known CVE</FormLabel>
                  <Popover open={cveOpen} onOpenChange={setCveOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant={"outline"} role={"combobox"}
                                className={cn(
                                  "w-[500px] justify-between",
                                  !field.value && "text-muted-foreground"
                                )}>{field.value ? field.value : "Select CVE"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[500px] p-0">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="Search cves..." onValueChange={searchCves}/>
                        <CommandList>
                          <CommandEmpty>No CVE found.</CommandEmpty>
                          <CommandGroup>
                            {knownCves.map((cve) => (
                              <CommandItem
                                value={cve.id}
                                key={cve.id}
                                onSelect={(currentValue) => {
                                  if (currentValue !== field.value) {
                                    form.setValue("known_cve", cve.id)
                                  } else {
                                    form.setValue("known_cve", undefined)
                                  }
                                  setCveOpen(false)
                                }}
                              >
                                <div>
                                  <div className={"font-medium mb-1"}>{cve.id}</div>
                                  <div className={"text-xs text-gray-700 max-h-12 overflow-hidden"}>
                                    {cve.short_description}
                                  </div>
                                </div>
                                <Check
                                  className={cn(
                                    "ml-auto",
                                    cve.id === field.value
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    If the vulnerability is known, please provide the CVE ID
                  </FormDescription>
                  <FormMessage/>
                </FormItem>
              )}/>
          ) : null}
          <FormField control={form.control} name={"description"}
                     render={({field}) => (
                       <FormItem>
                         <FormLabel>Description</FormLabel>
                         <FormControl>
                           <Textarea placeholder="Description" {...field} />
                         </FormControl>
                         <FormDescription>
                           Please provide a detailed description of the vulnerability
                         </FormDescription>
                         <FormMessage/>
                       </FormItem>
                     )}/>
          {reportForm.allow_attachments ? (
            <>
              <FormField control={form.control} name={"attachments"}
                         render={({field: {ref, name, onBlur, onChange}}) => (
                           <FormItem>
                             <FormLabel>Attachments</FormLabel>
                             <FormControl>
                               <Input type="file"
                                      multiple
                                      ref={ref}
                                      name={name}
                                      onBlur={onBlur}
                                      onChange={(e) => onChange([...Array.from(e.target.files ?? [])])}
                               />
                             </FormControl>
                             <FormDescription>
                               You can attach files to provide more information. Maximum file size is 10MB each.
                             </FormDescription>
                             <FormMessage/>
                           </FormItem>
                         )}/>
            </>
          ) : null}
          <Turnstile siteKey={settings.recaptcha.siteKey} onSuccess={(token) => {
            setToken(token)
          }}/>
          <Button type="submit">Submit</Button>
        </form>
      </Form>
      <PoweredBy/>
    </div>
  )
}
